import { BellIcon } from '@heroicons/react/24/outline';
import { BellIcon as SolidBell } from '@heroicons/react/24/solid';
import CT from '@marketplace-ai/tags';
import { useSelector } from '@xstate/react';
import { useId } from 'react';
import { Outlet } from 'react-router';
import { ActorRefFrom } from 'xstate';
import { UserContextV2 } from '~/app';
import { HeaderBrandMenu } from '~/components/BrandSwitcher';
import { NotificationBadge } from '~/components/NotificationBadge';
import { SiteFooter } from '~/components/footers/SiteFooter';
import { HeaderHelpMenu } from '~/components/headers/HeaderHelpMenu/HeaderHelpMenu';
import { HlxLogo } from '~/components/logos';
import { useBrandConfig } from '~/config/branding';
import { TutorialVideoButton } from '~/features/tutorials/TutorialVideoButton';
import { paths } from '~/routing';
import { notificationState } from '~/services/notifications/notification.machine';

export const MarketHeader = () => {
  const userRef = UserContextV2.useActorRef();
  const { notificationsRef } = useSelector(userRef, (s) => s.context);
  const isProvider = useSelector(userRef, (state) =>
    state.matches({ $_AUTHENTICATED: '$_AUTHENTICATED_PROVIDER' })
  );
  const isImpersonating = useSelector(userRef, (state) =>
    state.matches({ $_AUTHENTICATED: '$_AUTHENTICATED_IMPERSONATING' })
  );
  const { siteName } = useBrandConfig();

  return (
    <div className="flex flex-col h-full min-h-0">
      <nav
        aria-label={`${siteName} header`}
        className="h-[72px] w-full bg-white border-b border-grey-900 flex items-center justify-between px-6 max-[1360px]:pl-[58px] shrink-0"
      >
        {/* MCV-1513 brand switcher trigger, wrapping the existing HLX logo */}
        <HeaderBrandMenu
          brand="hlx"
          surface="market-header"
          primaryPath={paths.hlx.dashboard}
          primaryLabel="Go to dashboard"
          triggerAriaLabel={`${siteName} menu`}
          switchTargetPath={isProvider ? paths.vault.dashboard : undefined}
        >
          <HlxLogo height={30} />
        </HeaderBrandMenu>
        {/* The Provider HLX tour video (providers only), the help (?) menu, then the bell
            [MCV-1724] [MCV-1780] [MCV-1945] */}
        <div className="flex items-center gap-3 pr-2">
          {isProvider && <TutorialVideoButton videoId="providerHlxTour" tooltipAlign="end" />}
          <HeaderHelpMenu brand="hlx" canContactSupport={!isImpersonating} />
          {notificationsRef && <MarketNotificationBell notificationsRef={notificationsRef} />}
        </div>
      </nav>
      <div className="flex-1 min-h-0">
        <Outlet />
      </div>
      <SiteFooter compactOnMobile legalLinksInNewTab signedIn />
    </div>
  );
};

/**
 * Exported for `__tests__/MarketNotificationBell.browser.spec.tsx`, which pins
 * the accessible-name/description contract described in `NotificationBadge`.
 * Taking `notificationsRef` as a prop keeps it renderable from a stub actor,
 * without standing up UserContextV2 and Clerk.
 */
export const MarketNotificationBell = ({
  notificationsRef,
}: {
  notificationsRef: ActorRefFrom<typeof notificationState>;
}) => {
  const { isOpen, notifications } = useSelector(notificationsRef, (s) => s.context);
  const badgeId = useId();

  return (
    <button
      onClick={() => notificationsRef.send({ type: 'ON_TOGGLE_PANEL' })}
      className="relative p-1.5 hover:bg-grey-900/50 rounded-control transition-colors"
      aria-label="Toggle notifications"
      data-notification-toggle="true"
      aria-describedby={notifications.length > 0 ? badgeId : undefined}
    >
      {isOpen ? (
        <SolidBell className="size-5 text-grey-400" data-cy={CT.ToolBar.bellIcon} />
      ) : (
        <BellIcon className="size-5 text-grey-400" data-cy={CT.ToolBar.bellIcon} />
      )}
      <NotificationBadge count={notifications.length} id={badgeId} />
    </button>
  );
};
